/**
 * Failure-mode register — DR-2026-352 v0.5.0 (D10–D12).
 *
 * The s556 brief (ReviewQueue.jsx) applies: no tiles, no charts, no animation; one line
 * of counts; the reason a row is listed is IN the row; the default view is the working
 * catalogue — merged and retired hidden — with open 5a decisions first.
 *
 * READ-ONLY by design: dispositions are taken on /qms/review-queue (D3); modes,
 * occurrences and controls change only by tracked SQL under /deploy (D1). Every link out
 * of this page goes to where the act happens. D11: the queue chips are the QUEUE's rows,
 * read not recomputed. D10: a control never checked is never shown clean.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Header, Table, Button, Label, Loader, Message } from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';
import {
  SEVERITY_COLOR, MODE_STATUS_COLOR, fmtDate, caughtBySummary, controlsSummary,
  recurredChip, proposalChip,
} from './failureModeFormat';

const sub = { fontSize: '0.85em', color: '#777' };

export default function FailureModes() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [family, setFamily] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (showAll) p.set('status', 'all');
    if (family) p.set('family', family);
    setLoading(true);
    fetchApi(`${getApiPath()}/@qms/failure-modes?${p.toString()}`)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  }, [showAll, family]);

  useEffect(load, [load]);

  const items = data?.items || [];
  const t = data?.totals || {};
  const families = data?.families || [];
  const labels = Object.fromEntries((data?.detection || []).map((d) => [d.code, d.label]));

  return (
    <Container className="view-wrapper" style={{ paddingTop: '1rem' }}>
      <Helmet title="Failure modes" />
      <Link to="/qms">&larr; QMS</Link>
      <Header as="h1" style={{ marginBottom: '0.25rem' }}>Failure modes</Header>

      {/* One line of counts. The controls figure carries D10's register-wide state: a
          register whose controls were never checked says so here — not nothing. */}
      {data ? (
        <p style={{ color: '#555', marginBottom: '0.75rem' }}>
          <strong>{t.modes_open}</strong> modes ({t.modes} incl. merged/retired)
          {' · '}<strong>{t.occurrences}</strong> occurrences
          {' · '}<strong>{t.controls_live}</strong> live controls
          {t.controls_unchecked === t.controls_live ? (
            <> — <span style={{ color: '#777' }}>none checked yet</span></>
          ) : (
            <>
              {' — '}
              {t.controls_missing
                ? <strong style={{ color: '#a33' }}>{t.controls_missing} missing</strong>
                : 'all exist'}
              {t.controls_unchecked ? `, ${t.controls_unchecked} unchecked` : ''}
              {t.last_checked_at ? ` (checked ${fmtDate(t.last_checked_at)})` : ''}
            </>
          )}
          {' · '}
          <Link to="/qms/review-queue?type=failure_mode_recurred_after_control&class=all">
            <strong>{t.recurred_pending}</strong> 5a decisions pending
          </Link>
          {' · '}
          <Link to="/qms/review-queue?type=failure_mode_new&class=all">
            <strong>{t.proposals_pending}</strong> proposals pending
          </Link>
          {t.caught_by ? <> · caught by {caughtBySummary(t.caught_by, labels)}</> : null}
        </p>
      ) : null}

      <div style={{ marginBottom: '0.75rem' }}>
        {families.map((f) => (
          <Button
            key={f.code} size="tiny" basic={family !== f.code} primary={family === f.code}
            onClick={() => setFamily(family === f.code ? null : f.code)}
            title={`${f.label}${f.capa_id ? ` — ${f.capa_id}` : ' — no CAPA'}`}
          >
            {f.code}
          </Button>
        ))}
        <Button size="tiny" basic={!showAll} style={{ marginLeft: '1rem' }} onClick={() => setShowAll((s) => !s)}>
          {showAll ? 'hiding nothing' : 'show merged/retired'}
        </Button>
      </div>

      {error ? <Message negative onDismiss={() => setError(null)}>{error}</Message> : null}
      {loading ? <Loader active inline="centered" /> : null}

      {/* `!error` is load-bearing (the ReviewQueue lesson): a failed fetch must not render
          a reassuring empty state beneath its own error. */}
      {!loading && !error && !items.length ? (
        <Message positive>No failure mode in this view.</Message>
      ) : null}

      {items.length ? (
        <Table compact="very" size="small" basic="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={2}>Mode</Table.HeaderCell>
              <Table.HeaderCell width={5}>Name</Table.HeaderCell>
              <Table.HeaderCell width={2}>Occurrences</Table.HeaderCell>
              <Table.HeaderCell width={3}>Caught by</Table.HeaderCell>
              <Table.HeaderCell width={2}>Controls</Table.HeaderCell>
              <Table.HeaderCell width={2}>Queue</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.map((m) => {
              const ctl = controlsSummary(m);
              const rec = recurredChip(m.recurred);
              const prop = proposalChip(m.proposal);
              return (
                <Table.Row key={m.mode_id}>
                  <Table.Cell>
                    <Link to={`/qms/failure-modes/${m.mode_id}`}><code>{m.mode_id}</code></Link>
                    <div>
                      <Label size="mini" color={SEVERITY_COLOR[m.severity_class]} horizontal>{m.severity_class}</Label>
                      <Label size="mini" color={MODE_STATUS_COLOR[m.status]} horizontal>{m.status}</Label>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <Link to={`/qms/failure-modes/${m.mode_id}`}>{m.name}</Link>
                    <div style={sub}>
                      family {m.family} — {m.family_label}
                      {m.merged_into ? ` · merged into ${m.merged_into}` : ''}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <strong>{m.n_occurrences}</strong>
                    <div style={sub}>last {fmtDate(m.last_occurred_at)}</div>
                  </Table.Cell>
                  <Table.Cell style={{ fontSize: '0.9em' }}>{caughtBySummary(m.caught_by, labels)}</Table.Cell>
                  <Table.Cell><Label size="small" color={ctl.color} basic>{ctl.text}</Label></Table.Cell>
                  <Table.Cell>
                    {rec ? <Label size="small" color={rec.color}>{rec.text}</Label> : null}
                    {prop ? <Label size="small" color={prop.color} basic>{prop.text}</Label> : null}
                    {!rec && !prop ? <span style={{ color: '#999' }}>—</span> : null}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      ) : null}
    </Container>
  );
}
